import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { p as tp, useP } from '../../i18n/useP';
import { formatNumber, formatMoney } from '../../utils/format';
import {
  listHrContracts, listHrPeople, listPayrollMovements, peopleCostSynthesis,
  createPayrollMovement, updatePayrollMovement, deletePayrollMovement,
  listDeptRefSalaries, upsertDeptRefSalary,
} from '../../api/hr';
import { getChrYearly } from '../../api/chrYearly';
import { usePersistentValueLocal } from '../../hooks/usePersistentState';
import { horizonAfter, useReadVersion } from '../../components/ReportingControls';
import './HrPage.css';
import './PlanningTrajectory.css';

/* Trajectoire des effectifs — par département, par année (2026-08-22).

   ELLE N'A PAS SON PROPRE STOCKAGE, et c'est tout le cadrage. Poser
   « Data : 7,0 → 9,0 EPT en 2028 », c'est créer 2,0 EPT de POSITIONS OUVERTES
   datées du 1er janvier 2028, valorisées au salaire de référence du
   département. Baisser crée une RÉDUCTION GÉNÉRALE. Même table
   (payroll_simulation_movements), même moteur, même cascade de coût
   qu'ailleurs — et un mouvement pluriannuel s'efface comme n'importe quel
   autre. Aucun troisième moteur, aucun nominatif à cinq ans.

   FRONTIÈRE D'ÉDITION : les années couvertes par des contrats se LISENT, elles
   ne s'éditent pas. Là où l'on connaît les noms, on planifie par personne.
   La frontière n'est pas un choix d'écran, c'est la limite des contrats.

   L'ARGENT SE DÉDUIT DE L'EFFECTIF, mais on n'extrapole que l'ÉCART. Poser
   « brut = Σ EPT × salaire de référence » dérivait de 6 % contre le réel
   (3'019 kCHF estimés contre 3'227 réels sur KeyIT 2026) : une moyenne de
   personnes plein-an ne reconstitue pas une masse salariale. La base reste
   donc le RÉEL de la dernière année sous contrat, et seul l'écart d'effectif
   est valorisé au salaire de référence du département — ce qui est aussi la
   bonne économie : le coût marginal d'un poste, c'est son salaire.

   COÛT DE RÉFÉRENCE : la moyenne des personnes présentes DOUZE MOIS, ramenées
   à 100 %. Le rapport naïf « masse de l'année ÷ EPT de l'année » est faussé
   pour tout arrivant ou partant — sur KeyIT il donnait 219'780 CHF/EPT pour un
   département dont la seule personne est arrivée en avril, contre 129'118
   ailleurs. Et quand un département n'a personne sur douze mois, il n'y a rien
   à dériver : l'écran affiche « à saisir » plutôt qu'une moyenne inventée. */

const NO_DEPT = 'À définir';

export default function PlanningTrajectoryPage({ activeTenant, year, embedded, unit = 'chf', addToast }) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id;
  const N = year || new Date().getFullYear();
  // L'horizon de la version de plan lue (2026-10-11), au moins une année.
  const horizon = Math.max(1, horizonAfter(useReadVersion(), N));

  const [people, setPeople] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [chr, setChr] = useState(null);
  // Salaires de référence MANUELS (lacunes) — le dérivé prime toujours.
  const [manualRef, setManualRef] = useState({});
  // Synthèses RÉELLES des années sous contrat (brut, charges, frais).
  const [synth, setSynth] = useState({});
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(0);
  /* Indexation annuelle des salaires — un taux PAR ANNÉE (proto 2026-09-01,
     ex-taux unique) : la carte Hypothèses les aligne, et le facteur d'une
     année est le produit des taux qui la précèdent. Toujours global à tous
     les départements : un taux par département coûterait plus en saisie
     qu'il ne rapporte en précision. */
  const [indexByYear, setIndexByYear] = usePersistentValueLocal(`hr_traj_index_v2_${tenantId || ''}`, {});
  const pctOf = (y) => Number(indexByYear[y]) || 0;

  useEffect(() => {
    if (!tenantId) return;
    let alive = true;
    setLoading(true);
    Promise.all([
      listHrPeople(tenantId), listHrContracts(tenantId),
      listPayrollMovements(tenantId), getChrYearly(tenantId, N),
      listDeptRefSalaries(tenantId),
    ]).then(([pe, co, mv, cy, rs]) => {
      if (!alive) return;
      setPeople(pe.error ? [] : (pe.data || []));
      setContracts(co.error ? [] : (co.data || []));
      setMovements(mv.error ? [] : (mv.data || []));
      setChr(cy.error ? null : cy.data);
      const m = {};
      (rs.error ? [] : (rs.data || [])).forEach((r) => { m[r.department] = Number(r.annual_salary); });
      setManualRef(m);
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [tenantId, N, reload]);

  /* Dernière année COUVERTE PAR UN CONTRAT : au-delà, plus personne n'est sous
     contrat, et l'édition agrégée reprend la main. Un contrat sans date de fin
     court indéfiniment — il ne repousse donc pas la frontière. */
  const lastContractYear = useMemo(() => {
    let y = N;
    contracts.forEach((c) => {
      if (!c.end_date) return;
      const ey = Number(String(c.end_date).slice(0, 4));
      if (ey > y) y = ey;
    });
    return y;
  }, [contracts, N]);

  const years = useMemo(
    () => Array.from({ length: horizon + 1 }, (_, i) => N + i),
    [N, horizon],
  );

  /* Années sous contrat : on lit la synthèse RÉELLE. Au-delà, le moteur
     dégrade en silence (les taux LPP n'existent pas encore : les charges
     tombaient à 6,4 % en 2028 contre 16,9 % en 2027) — on ne l'interroge donc
     pas, on extrapole explicitement à partir de l'année de référence. */
  const refYear = Math.min(lastContractYear, years[years.length - 1]);
  useEffect(() => {
    if (!tenantId) return;
    let alive = true;
    const ys = years.filter((y) => y <= lastContractYear);
    /* La route répond l'objet NU (response_model FastAPI) ; d'autres routes
       enveloppent dans {data}. On accepte les deux — r.data était undefined
       ici, et chaque masse affichait « — ». */
    Promise.all(ys.map((y) => peopleCostSynthesis(tenantId, y)
      .then((r) => [y, (r && r.rows) ? r : ((r && r.data && r.data.rows) ? r.data : null)])))
      .then((pairs) => {
        if (!alive) return;
        const m = {};
        pairs.forEach(([y, d]) => { if (d) m[y] = d; });
        setSynth(m);
      });
    return () => { alive = false; };
  }, [tenantId, lastContractYear, years, reload]);

  /* Facteur d'indexation composé : Π (1 + taux) des années (refYear, y]. */
  const idxFactor = (y) => {
    let f = 1;
    for (let k = refYear + 1; k <= y; k += 1) f *= 1 + pctOf(k) / 100;
    return f;
  };

  /* Département d'une personne = celui de son contrat le plus récent. */
  const deptOf = useMemo(() => {
    const m = new Map();
    contracts.forEach((c) => {
      if (!c.person_id) return;
      const cur = m.get(c.person_id);
      if (!cur || (c.start_date || '') > cur.start) m.set(c.person_id, { start: c.start_date || '', d: c.department || '' });
    });
    return (id) => (m.get(id)?.d || '');
  }, [contracts]);

  /* EPT contractuel d'un département sur une année : moyenne des douze mois
     des taux de contrat. Les mouvements NOMINATIFS de l'année en font partie
     — mais pas les mouvements agrégés, qui sont justement ce qu'on édite. */
  const contractFte = useMemo(() => {
    const byPerson = new Map();
    contracts.forEach((c) => {
      if (!c.person_id) return;
      if (!byPerson.has(c.person_id)) byPerson.set(c.person_id, []);
      byPerson.get(c.person_id).push(c);
    });
    const active = (c, y, m) => {
      if (!c.start_date) return false;
      const ms = new Date(y, m, 1), me = new Date(y, m + 1, 0);
      if (new Date(c.start_date) > me) return false;
      if (c.end_date && new Date(c.end_date) < ms) return false;
      return true;
    };
    return (dept, y) => {
      let s = 0;
      people.forEach((pp) => {
        if ((deptOf(pp.id) || NO_DEPT) !== dept) return;
        const pc = byPerson.get(pp.id) || [];
        for (let m = 0; m < 12; m += 1) {
          const c = pc.filter((x) => active(x, y, m))
            .sort((a, b) => (a.end_date ? 1 : -1) - (b.end_date ? 1 : -1))[0];
          if (c) s += Number(c.activity_rate || 0);
        }
      });
      return s / 12;
    };
  }, [people, contracts, deptOf]);

  /* Salaire de référence d'un EPT à 100 %, par département — moyenne des
     personnes présentes DOUZE MOIS. Aucune ⇒ pas de coût, « à saisir ». */
  const refSalary = useMemo(() => {
    const acc = new Map();
    (chr?.rows || []).forEach((r) => {
      const f = Number(r.fte_pct) / 100;
      if (!f || Number(r.months_in_year) < 12) return;
      const d = r.department || NO_DEPT;
      if (!acc.has(d)) acc.set(d, []);
      acc.get(d).push(Number(r.gross_salary) / f);
    });
    const out = new Map();
    acc.forEach((arr, d) => out.set(d, arr.reduce((a, b) => a + b, 0) / arr.length));
    /* Le MANUEL ne comble que les lacunes : là où une moyenne se dérive,
       la saisie ne compte pas. */
    Object.entries(manualRef).forEach(([d, v]) => {
      if (!out.has(d) && v > 0) out.set(d, v);
    });
    return out;
  }, [chr, manualRef]);

  /* Mouvements AGRÉGÉS déjà posés : un par (département, année), reconnu à sa
     date au 1er janvier d'une année au-delà de la frontière contractuelle. */
  const stratMoves = useMemo(() => {
    const m = new Map();
    movements.forEach((mv) => {
      if (mv.person_id) return;
      const s = String(mv.start_month || '');
      if (!/-01-01$/.test(s)) return;
      const y = Number(s.slice(0, 4));
      if (y <= lastContractYear) return;
      m.set(`${mv.department || NO_DEPT}|${y}`, mv);
    });
    return m;
  }, [movements, lastContractYear]);

  const depts = useMemo(() => {
    const s = new Set();
    people.forEach((pp) => s.add(deptOf(pp.id) || NO_DEPT));
    movements.forEach((mv) => { if (!mv.person_id) s.add(mv.department || NO_DEPT); });
    return [...s].sort((a, b) => a.localeCompare(b, 'fr'));
  }, [people, movements, deptOf]);

  /* EPT affiché : contractuel jusqu'à la frontière, puis l'année précédente
     PLUS le mouvement agrégé de l'année — la trajectoire est incrémentale. */
  const fteAt = (dept, y) => {
    if (y <= lastContractYear) return contractFte(dept, y);
    const prev = fteAt(dept, y - 1);
    const mv = stratMoves.get(`${dept}|${y}`);
    if (!mv) return prev;
    const sign = mv.movement_type === 'departure' ? -1 : 1;
    return prev + sign * Number(mv.fte || 0);
  };

  const setTarget = async (dept, y, value) => {
    const prev = fteAt(dept, y - 1);
    const delta = Math.round((value - prev) * 100) / 100;
    const key = `${dept}|${y}`;
    const existing = stratMoves.get(key);
    const ref = refSalary.get(dept);
    if (Math.abs(delta) < 0.005) {
      if (existing) await deletePayrollMovement(tenantId, existing.id);
      setReload((k) => k + 1);
      return;
    }
    if (ref == null) {
      addToast && addToast(p('Salaire de référence manquant pour ce département — impossible de chiffrer.'), 'error');
      return;
    }
    // Le salaire porté par le mouvement est le BRUT mensuel de l'effectif
    // ajouté (ou retiré), indexé des taux annuels depuis l'année de
    // référence. Les charges sont ajoutées par la cascade, comme partout.
    const idx = idxFactor(y);
    const payload = {
      movement_type: delta > 0 ? 'hire' : 'departure',
      start_month: `${y}-01-01`,
      end_month: null,
      fte: Math.abs(delta),
      monthly_amount: Math.round(Math.abs(delta) * (ref / 12) * idx),
      label: `${dept} · ${y}`,
      department: dept === NO_DEPT ? null : dept,
    };
    if (existing) await updatePayrollMovement(tenantId, existing.id, payload);
    else await createPayrollMovement(tenantId, payload);
    setReload((k) => k + 1);
  };

  /* ── L'argent ────────────────────────────────────────────────────────────
     Années sous contrat : la synthèse RÉELLE, telle qu'ailleurs dans People.
     Au-delà : la base réelle de l'année de référence, PLUS l'écart d'effectif
     valorisé au salaire de référence du département, le tout indexé. Les
     charges suivent le taux constaté ; les frais suivent l'effectif. */
  const num = (o, k) => Number(o && o[k]) || 0;
  /* Masse brute d'un département : réel = Σ des rémunérations effectives de
     ses personnes ; au-delà, la base réelle du département plus l'écart
     d'effectif valorisé au salaire de référence, le tout indexé. */
  const deptGrossReal = (d, y) => {
    const rows = (synth[y] && synth[y].rows) || [];
    return rows.reduce((a, r) => a + ((r.department || NO_DEPT) === d ? Number(r.effective_remuneration || 0) : 0), 0);
  };
  const deptGross = (d, y) => {
    if (y <= lastContractYear) return synth[y] ? deptGrossReal(d, y) : null;
    if (!synth[refYear]) return null;
    const base = deptGrossReal(d, refYear);
    const r = refSalary.get(d);
    const delta = r == null ? 0 : (fteAt(d, y) - fteAt(d, refYear)) * r;
    return (base + delta) * idxFactor(y);
  };
  const fteTotal = (y) => depts.reduce((a, d) => a + fteAt(d, y), 0);
  const refS = synth[refYear];
  const refGross = num(refS, 'total_effective_remuneration');
  const refCsRate = refGross > 0 ? num(refS, 'total_social_empr') / refGross : 0;
  const refFees = num(refS, 'total_other_fees');
  const refFte = fteTotal(refYear);

  const money = (y) => {
    if (y <= lastContractYear) {
      const d = synth[y];
      if (!d) return null;
      return {
        real: true,
        gross: num(d, 'total_effective_remuneration'),
        cs: num(d, 'total_social_empr'),
        fees: num(d, 'total_other_fees'),
        cost: num(d, 'total_cost_empr'),
      };
    }
    if (!refS) return null;
    const idx = idxFactor(y);
    // On valorise l'ÉCART d'effectif, pas l'effectif : une moyenne de
    // personnes plein-an ne reconstitue pas une masse salariale (−6 % de
    // dérive constatée). Un département sans salaire de référence ne peut pas
    // bouger — l'édition y est déjà refusée, son écart reste donc nul.
    const delta = depts.reduce((a, d) => {
      const r = refSalary.get(d);
      if (r == null) return a;
      return a + (fteAt(d, y) - fteAt(d, refYear)) * r;
    }, 0);
    const gross = (refGross + delta) * idx;
    const cs = gross * refCsRate;
    const fees = refFte > 0 ? refFees * (fteTotal(y) / refFte) * idx : 0;
    return { real: false, gross, cs, fees, cost: gross + cs + fees };
  };

  // Dérivé ? (présents douze mois) — sinon la cellule de référence se SAISIT.
  const derivedDepts = useMemo(() => {
    const set = new Set();
    (chr?.rows || []).forEach((r) => {
      if (Number(r.fte_pct) && Number(r.months_in_year) >= 12) set.add(r.department || NO_DEPT);
    });
    return set;
  }, [chr]);
  const saveManualRef = async (d, v) => {
    const val = Number(v);
    await upsertDeptRefSalary(tenantId, d === NO_DEPT ? NO_DEPT : d, Number.isNaN(val) ? 0 : val);
    setReload((k) => k + 1);
  };

  if (!tenantId) return <main className="hr-page"><div className="hr-page__empty">{p('Sélectionner une entreprise.')}</div></main>;
  if (loading) return <main className="hr-page"><div className="hr-page__loading">{t('common:status.loading')}</div></main>;

  const mny = (v) => formatMoney(v, unit, 0);
  const missing = depts.filter((d) => refSalary.get(d) == null);

  const lastYear = years[years.length - 1];
  /* Table condensée sur le RÉEL seulement : première année et dernière année
     sous contrat (les années de contrat intermédiaires n'apprennent rien de
     neuf ici) — mais TOUTES les années à poser restent visibles et éditables
     (correction du 2026-09-01 : 2028-2030 avaient disparu avec elles). */
  const shownYears = [...new Set([
    N, lastContractYear,
    ...years.filter((y) => y > lastContractYear),
  ])].filter((y) => y >= N && y <= lastYear).sort((a, b) => a - b);
  const evolFte = (d) => fteAt(d, lastYear) - fteAt(d, N);
  const evolGross = (d) => {
    const a = deptGross(d, N), b = deptGross(d, lastYear);
    return (a == null || b == null) ? null : b - a;
  };
  const totFteAt = (y) => fteTotal(y);
  const moneyByYear = years.map((y) => [y, money(y)]);
  const firstMoney = moneyByYear[0][1];
  const lastMoney = moneyByYear[moneyByYear.length - 1][1];
  const costEvol = (firstMoney && lastMoney) ? lastMoney.cost - firstMoney.cost : null;
  /* Signe et couleur d'une évolution : sage en hausse, terracotta en baisse. */
  const evCls = (v) => (Math.abs(v) < 0.005 ? '' : (v > 0 ? ' is-up' : ' is-dn'));
  const evTxt = (v, dec, unitLbl = '') =>
    `${v > 0 ? '+' : v < 0 ? '−' : ''}${formatNumber(Math.abs(v), dec)}${unitLbl}`;

  /* Répartition du dernier horizon, triée décroissante. */
  const lastTotal = totFteAt(lastYear);
  const repartition = depts
    .map((d) => ({ d, v: fteAt(d, lastYear) }))
    .filter((r) => r.v > 0.004)
    .sort((a, b) => b.v - a.v);

  /* Courbe des EPT : échelle simple de 0 au max. */
  const fteSeries = years.map((y) => totFteAt(y));
  const fteMax = Math.max(1, ...fteSeries);

  return (
    <main className={`hr-page${embedded ? ' is-embedded' : ''}`}>
      <div className="trj">
        {/* ── Hypothèses générales : l'indexation PAR ANNÉE, et le taux de
            charges sociales CONSTATÉ (pas une hypothèse à taper). ── */}
        <div className="trj__hypo trj__card">
          <div>
            <div className="trj__hypo-lbl">{p('Indexation annuelle des salaires')}</div>
            <div className="trj__hypo-years">
              {years.filter((y) => y > refYear).map((y) => (
                <label key={y}>
                  <span>{y}</span>
                  <input type="number" step="0.1" value={pctOf(y) || ''}
                         placeholder="0"
                         onChange={(e) => setIndexByYear({ ...indexByYear, [y]: e.target.value === '' ? 0 : parseFloat(e.target.value) })} />
                  <em>%</em>
                </label>
              ))}
            </div>
          </div>
          <div>
            <div className="trj__hypo-lbl">{p('Charges sociales employeur')}</div>
            <div className="trj__hypo-cs">
              <b>{formatNumber(refCsRate * 100, 1)} %</b>
              <span>{p('constaté {{year}}, appliqué au-delà', { year: refYear })}</span>
            </div>
          </div>
        </div>

        <div className="trj__card trj__card--table">
        <div className="trj__tablehead">
          <span className="trj__hypo-lbl" style={{ marginBottom: 0 }}>{p('Effectifs (EPT) et masse salariale')}</span>
        </div>
        <div className="trj__scroll">
          {/* Colonnes HOMOGÈNES, table figée : le libellé et la référence ont
              leur largeur, chaque paire EPT · Masse (années et Évolution) se
              partage le reste à parts égales — la table tient l'écran. */}
          <table className="trj__table trj__table--fixed">
            <colgroup>
              <col style={{ width: 150 }} />
              {Array.from({ length: (shownYears.length + 1) * 2 }, (_, i) => (
                <col key={i} style={{ width: `calc((100% - 150px - 110px) / ${(shownYears.length + 1) * 2})` }} />
              ))}
              <col style={{ width: 110 }} />
            </colgroup>
            <thead>
              <tr>
                <th className="trj__lbl" rowSpan={2}>{p('Département')}</th>
                {shownYears.map((y) => (
                  <th key={y} colSpan={2}
                      className={y <= lastContractYear ? 'trj__y trj__y--contract' : 'trj__y trj__y--strat'}>
                    {y}
                    <small>{y <= lastContractYear ? p('contrats') : p('à poser')}</small>
                  </th>
                ))}
                <th className="trj__y trj__y--evol" colSpan={2}>{p('Évolution')}<small>{N}–{lastYear}</small></th>
                <th className="trj__y" rowSpan={2}>{p('Salaire réf. 100 %')}</th>
              </tr>
              <tr>
                {shownYears.map((y) => (
                  <React.Fragment key={y}>
                    <th className={`trj__sub${y <= lastContractYear ? ' trj__sub--contract' : ' trj__sub--strat'}`}>EPT</th>
                    <th className="trj__sub">{p('Masse')}</th>
                  </React.Fragment>
                ))}
                <th className="trj__sub trj__sub--evol">EPT</th>
                <th className="trj__sub trj__sub--evol">{p('Masse')}</th>
              </tr>
            </thead>
            <tbody>
              {depts.map((d) => {
                const ref = refSalary.get(d);
                const eF = evolFte(d);
                const eG = evolGross(d);
                return (
                  <tr key={d}>
                    <td className="trj__lbl">{d}</td>
                    {shownYears.map((y) => {
                      const v = fteAt(d, y);
                      const g = deptGross(d, y);
                      const gCell = (
                        <td key={`g${y}`} className={`trj__cell trj__cell--money${y > lastContractYear ? ' trj__cell--est' : ''}`}>
                          {g == null ? '—' : mny(g)}
                        </td>
                      );
                      if (y <= lastContractYear) {
                        return (
                          <React.Fragment key={y}>
                            <td className="trj__cell trj__cell--contract trj__cell--first">{formatNumber(v, 1)}</td>
                            {gCell}
                          </React.Fragment>
                        );
                      }
                      const delta = v - fteAt(d, y - 1);
                      return (
                        <React.Fragment key={y}>
                          <td className="trj__cell trj__cell--first">
                            <input
                              type="number" step="0.1" min="0" defaultValue={v.toFixed(1)}
                              disabled={ref == null}
                              title={ref == null ? p('Salaire de référence manquant') : undefined}
                              onBlur={(e) => {
                                const nv = parseFloat(e.target.value);
                                if (!Number.isNaN(nv) && Math.abs(nv - v) >= 0.005) setTarget(d, y, nv);
                              }}
                            />
                            {Math.abs(delta) >= 0.005 && (
                              <span className={`trj__gen ${delta > 0 ? 'is-up' : 'is-dn'}`}>
                                {delta > 0
                                  ? `＋${formatNumber(delta, 1)}`
                                  : `−${formatNumber(Math.abs(delta), 1)}`}
                              </span>
                            )}
                          </td>
                          {gCell}
                        </React.Fragment>
                      );
                    })}
                    <td className={`trj__cell trj__cell--evol trj__cell--first${evCls(eF)}`}>{evTxt(eF, 1)}</td>
                    <td className={`trj__cell trj__cell--evol${eG == null ? '' : evCls(eG)}`}>{eG == null ? '—' : evTxt(eG, 0)}</td>
                    <td className="trj__cell trj__cell--ref">
                      {derivedDepts.has(d)
                        ? mny(ref)
                        : (
                          /* Lacune : la référence se SAISIT (annuel 100 %, CHF).
                             Effacer = retirer la saisie. */
                          <input
                            className="trj__ref-input"
                            type="number" min="0" step="1000"
                            defaultValue={manualRef[d] || ''}
                            placeholder={p('à saisir')}
                            title={p('Salaire annuel de référence à 100 % (CHF) — aucune personne présente douze mois, rien ne se dérive.')}
                            onBlur={(e) => {
                              const nv = e.target.value === '' ? 0 : parseFloat(e.target.value);
                              if ((manualRef[d] || 0) !== nv) saveManualRef(d, nv);
                            }}
                          />
                        )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td className="trj__lbl">{p('Total')}</td>
                {shownYears.map((y) => {
                  const m = money(y);
                  return (
                    <React.Fragment key={y}>
                      <td className="trj__cell trj__cell--total trj__cell--first">{formatNumber(totFteAt(y), 1)}</td>
                      <td className="trj__cell trj__cell--total">{m ? mny(m.gross) : '—'}</td>
                    </React.Fragment>
                  );
                })}
                <td className={`trj__cell trj__cell--total trj__cell--first${evCls(totFteAt(lastYear) - totFteAt(N))}`}>
                  {evTxt(totFteAt(lastYear) - totFteAt(N), 1)}
                </td>
                <td className={`trj__cell trj__cell--total${(firstMoney && lastMoney) ? evCls(lastMoney.gross - firstMoney.gross) : ''}`}>
                  {(firstMoney && lastMoney) ? evTxt(lastMoney.gross - firstMoney.gross, 0) : '—'}
                </td>
                <td className="trj__cell" />
              </tr>
            </tfoot>
          </table>
        </div>
        </div>

        {/* ── Trois lectures : l'employeur, la courbe, la répartition ── */}
        <div className="trj__trio">
          <div className="trj__panel">
            <div className="trj__hypo-lbl">{p('Synthèse impact employeur')}</div>
            <table className="trj__mini">
              <thead>
                <tr>
                  <th>{p('Année')}</th><th>{p('Masse')}</th>
                  <th>{p('Charges sociales')}</th><th>{p('Frais')}</th><th>{p('Coût total')}</th>
                </tr>
              </thead>
              <tbody>
                {moneyByYear.map(([y, m]) => (
                  <tr key={y} className={y > lastContractYear ? 'is-est' : ''}>
                    <td>{y}</td>
                    <td>{m ? mny(m.gross) : '—'}</td>
                    <td>{m ? mny(m.cs) : '—'}</td>
                    <td>{m ? mny(m.fees) : '—'}</td>
                    <td className="trj__mini-strong">{m ? mny(m.cost) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {costEvol != null && (
              <div className={`trj__evolband${evCls(costEvol)}`}>
                <span>{p('Évolution')} {N}–{lastYear}</span>
                <b>{evTxt(costEvol, 0)} ({evTxt(firstMoney.cost ? (costEvol / firstMoney.cost) * 100 : 0, 1, ' %')})</b>
              </div>
            )}
          </div>

          <div className="trj__panel">
            <div className="trj__hypo-lbl">{p('Évolution des EPT')}</div>
            <svg className="trj__chart" viewBox="0 0 260 130" role="img" aria-label={p('Évolution des EPT')}>
              {(() => {
                const x = (i) => 18 + (i * (260 - 36)) / Math.max(1, years.length - 1);
                const yPos = (v) => 110 - (v / fteMax) * 85;
                const pts = fteSeries.map((v, i) => `${x(i)},${yPos(v)}`).join(' ');
                const bIdx = years.findIndex((y) => y > lastContractYear);
                return (
                  <>
                    {bIdx > 0 && (
                      <line x1={(x(bIdx - 1) + x(bIdx)) / 2} y1={10} x2={(x(bIdx - 1) + x(bIdx)) / 2} y2={116}
                            stroke="var(--color-accent-soft)" strokeDasharray="3 3" />
                    )}
                    <polyline points={pts} fill="none" stroke="var(--color-accent)" strokeWidth="2" />
                    {fteSeries.map((v, i) => (
                      <g key={years[i]}>
                        <circle cx={x(i)} cy={yPos(v)} r="3"
                                fill={years[i] <= lastContractYear ? 'var(--color-navy)' : 'var(--color-accent)'} />
                        <text x={x(i)} y={yPos(v) - 8} textAnchor="middle" fontSize="9"
                              fill="var(--color-navy)">{formatNumber(v, 1)}</text>
                        <text x={x(i)} y={126} textAnchor="middle" fontSize="8.5"
                              fill="var(--color-text-muted)">{years[i]}</text>
                      </g>
                    ))}
                  </>
                );
              })()}
            </svg>
          </div>

          <div className="trj__panel">
            <div className="trj__hypo-lbl">{p('Répartition EPT')} · {lastYear}</div>
            <div className="trj__bars">
              {repartition.map(({ d, v }) => (
                <div key={d}>
                  <div className="trj__bar-head">
                    <span>{d}</span>
                    <span>{lastTotal > 0 ? formatNumber((v / lastTotal) * 100, 1) : 0} %</span>
                  </div>
                  <div className="trj__bar-track">
                    <div className="trj__bar-fill" style={{ width: `${lastTotal > 0 ? (v / lastTotal) * 100 : 0}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </main>
  );
}
